// Hand-maintained — override generation
// Hand-maintained — safe to edit directly. See lib/gpt_core/sdk/CLAUDE.md for context.
// mix update.sdks preserves this file (only overwrites empty stubs). Do NOT add `return {};`.

import {
  getAdminSocialAccountsWorkspaceByWorkspaceId,
  getAdminSocialAccountsPlatformByPlatform,
  getAdminSocialAccountsById,
  postAdminSocialAccounts,
  patchAdminSocialAccountsById,
  deleteAdminSocialAccountsById,
  patchAdminSocialAccountsByIdEnablePosting,
  patchAdminSocialAccountsByIdDisablePosting,
  patchAdminSocialAccountsByIdDeactivate,
  postAdminSocialPosts,
  getAdminSocialPostsById,
  patchAdminSocialPostsById,
  deleteAdminSocialPostsById,
  patchAdminSocialPostsByIdSchedule,
  patchAdminSocialPostsByIdPublish,
  patchAdminSocialPostsByIdCancel,
  patchAdminSocialPostsByIdRetry,
  patchAdminSocialPostsByIdSeo,
  getAdminSocialPostsScheduled,
  getAdminSocialPostsAccountBySocialAccountId,
  getAdminSocialPostsCampaignBySocialCampaignId,
  getAdminSocialMetricsById,
  getAdminSocialMetricsPostBySocialPostId,
  getAdminSocialMetricsPostBySocialPostIdLatest,
  getAdminSocialMetricsAccountBySocialAccountId,
  getAdminSocialMetricsCampaignBySocialCampaignId,
  getAdminSocialPostsWorkspaceByWorkspaceId,
  getAdminSocialCampaigns,
  getAdminSocialCampaignsById,
  postAdminSocialCampaigns,
  patchAdminSocialCampaignsById,
  deleteAdminSocialCampaignsById,
  patchAdminSocialCampaignsByIdSchedule,
  patchAdminSocialCampaignsByIdCancel,
  postAdminSocialCampaignsByIdGenerateMasterCopy,
  postAdminSocialCampaignsByIdAdaptForPlatforms,
  postAdminSocialCampaignsByIdPreviewAdaptations,
  getAdminSocialTrendingHistoryById,
  getAdminSocialTrendingHistoryWorkspaceByWorkspaceId,
  postAdminSocialTrendingHistory,
  deleteAdminSocialTrendingHistoryById,
  getAdminSocialTrendingItemsById,
  getAdminSocialTrendingItemsSnapshotBySnapshotId,
  getAdminSocialTrendingWatchesById,
  getAdminSocialTrendingWatchesWorkspaceByWorkspaceId,
  postAdminSocialTrendingWatches,
  patchAdminSocialTrendingWatchesById,
  deleteAdminSocialTrendingWatchesById,
  patchAdminSocialTrendingWatchesByIdMarkTriggered,
  getAdminSocialTrendingHistoryRange,
} from "../_internal/sdk.gen";
import type { RequestOptions } from "../base-client";
import { RequestBuilder } from "../request-builder";

// ── Typed attribute interfaces ──────────────────────────────────────────────
// These match the `accept` lists on the corresponding Ash resource actions.
// workspace_id is NEVER an accepted attribute on CRUD actions — it is resolved
// from the authenticated actor's context.

/** Attributes accepted by `POST /social/accounts` (create action). */
export interface SocialAccountCreateAttributes {
  platform: string;
  platform_user_id?: string;
  display_name?: string;
  avatar_url?: string;
  account_type?: string;
  is_active?: boolean;
  posting_enabled?: boolean;
  metadata?: Record<string, unknown>;
}

/** Attributes accepted by `PATCH /social/accounts/:id` (update action). */
export interface SocialAccountUpdateAttributes {
  display_name?: string;
  avatar_url?: string;
  metadata?: Record<string, unknown>;
  last_verified_at?: string;
}

/** Attributes accepted by `POST /social/posts` (create action). */
export interface SocialPostCreateAttributes {
  platform: string;
  content: string;
  media_urls?: string[];
  hashtags?: string[];
  link_url?: string;
  link_metadata?: Record<string, unknown>;
  max_retries?: number;
  social_account_id: string;
  social_campaign_id?: string;
}

/** Attributes accepted by `PATCH /social/posts/:id` (update action). */
export interface SocialPostUpdateAttributes {
  content?: string;
  media_urls?: string[];
  hashtags?: string[];
  link_url?: string;
  link_metadata?: Record<string, unknown>;
  platform_metadata?: Record<string, unknown>;
}

/** Attributes accepted by `POST /social/campaigns` (create action). */
export interface SocialCampaignCreateAttributes {
  /** Required. Campaign name. */
  name: string;
  /** Content brief that the AI will use to generate master copy. */
  content_brief?: string;
  /** Target platforms, e.g. ["twitter", "linkedin", "instagram"]. */
  target_platforms?: string[];
  /** Media URLs to include in generated posts. */
  media_urls?: string[];
  /** SEO keywords for content optimization. */
  seo_keywords?: string[];
  /** Optional brand identity UUID for tone/style guidance. */
  brand_identity_id?: string;
}

/** Attributes accepted by `PATCH /social/campaigns/:id` (update action). */
export interface SocialCampaignUpdateAttributes {
  name?: string;
  content_brief?: string;
  target_platforms?: string[];
  media_urls?: string[];
  seo_keywords?: string[];
  brand_identity_id?: string;
}

/**
 * Social media management namespace — accounts, posts, metrics, and campaigns.
 *
 * Covers the full social publishing lifecycle: connect accounts, create and
 * schedule posts, collect engagement metrics, and run AI-powered social campaigns.
 *
 * @example
 * ```typescript
 * const admin = new GptAdmin({ apiKey: 'sk_srv_...' });
 *
 * // List connected accounts
 * const accounts = await admin.social.accounts.listByWorkspace('ws_abc');
 *
 * // Create and schedule a post
 * const post = await admin.social.posts.create({ ... });
 * await admin.social.posts.schedule(post.id, '2026-03-10T12:00:00Z');
 *
 * // Get latest metrics
 * const metrics = await admin.social.metrics.latestForPost(post.id);
 * ```
 */
export function createSocialNamespace(rb: RequestBuilder) {
  return {
    /** Social account management — connect, enable/disable, and deactivate accounts. */
    accounts: {
      /** List social accounts for a workspace. */
      listByWorkspace: async (
        workspaceId: string,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          getAdminSocialAccountsWorkspaceByWorkspaceId,
          { path: { workspace_id: workspaceId } },
          options,
        );
      },

      /** List social accounts by platform (e.g. "twitter", "instagram"). */
      listByPlatform: async (platform: string, options?: RequestOptions) => {
        return rb.execute(
          getAdminSocialAccountsPlatformByPlatform,
          { path: { platform } },
          options,
        );
      },

      /** Get a single social account by ID. */
      get: async (id: string, options?: RequestOptions) => {
        return rb.execute(
          getAdminSocialAccountsById,
          { path: { id } },
          options,
        );
      },

      /** Connect a new social account. */
      create: async (
        attributes: SocialAccountCreateAttributes,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          postAdminSocialAccounts,
          {
            body: {
              data: { type: "social-account", attributes },
            },
          },
          options,
        );
      },

      /** Update a social account. */
      update: async (
        id: string,
        attributes: SocialAccountUpdateAttributes,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          patchAdminSocialAccountsById,
          {
            path: { id },
            body: {
              data: { type: "social-account", id, attributes },
            },
          },
          options,
        );
      },

      /** Delete a social account. */
      delete: async (id: string, options?: RequestOptions) => {
        return rb.execute(
          deleteAdminSocialAccountsById,
          { path: { id } },
          options,
        );
      },

      /** Enable posting on an account. */
      enablePosting: async (id: string, options?: RequestOptions) => {
        return rb.execute(
          patchAdminSocialAccountsByIdEnablePosting,
          {
            path: { id },
            body: {
              data: { type: "social-account", id, attributes: {} },
            },
          },
          options,
        );
      },

      /** Disable posting on an account. */
      disablePosting: async (id: string, options?: RequestOptions) => {
        return rb.execute(
          patchAdminSocialAccountsByIdDisablePosting,
          {
            path: { id },
            body: {
              data: { type: "social-account", id, attributes: {} },
            },
          },
          options,
        );
      },

      /** Deactivate (disconnect) an account. */
      deactivate: async (id: string, options?: RequestOptions) => {
        return rb.execute(
          patchAdminSocialAccountsByIdDeactivate,
          {
            path: { id },
            body: {
              data: { type: "social-account", id, attributes: {} },
            },
          },
          options,
        );
      },
    },

    /** Social post management — create, schedule, publish, cancel, and retry posts. */
    posts: {
      /** Create a new social post draft. */
      create: async (
        attributes: SocialPostCreateAttributes,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          postAdminSocialPosts,
          {
            body: {
              data: { type: "social-post", attributes },
            },
          },
          options,
        );
      },

      /** Get a single post by ID. */
      get: async (id: string, options?: RequestOptions) => {
        return rb.execute(getAdminSocialPostsById, { path: { id } }, options);
      },

      /** Update a draft post. */
      update: async (
        id: string,
        attributes: SocialPostUpdateAttributes,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          patchAdminSocialPostsById,
          {
            path: { id },
            body: {
              data: { type: "social-post", id, attributes },
            },
          },
          options,
        );
      },

      /** Delete a post. */
      delete: async (id: string, options?: RequestOptions) => {
        return rb.execute(
          deleteAdminSocialPostsById,
          { path: { id } },
          options,
        );
      },

      /** Schedule a post for future publishing. */
      schedule: async (
        id: string,
        scheduledAt: string,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          patchAdminSocialPostsByIdSchedule,
          {
            path: { id },
            body: {
              data: {
                type: "social-post",
                id,
                attributes: { scheduled_at: scheduledAt },
              },
            },
          },
          options,
        );
      },

      /** Publish a post immediately. */
      publishNow: async (id: string, options?: RequestOptions) => {
        return rb.execute(
          patchAdminSocialPostsByIdPublish,
          {
            path: { id },
            body: {
              data: { type: "social-post", id, attributes: {} },
            },
          },
          options,
        );
      },

      /** Cancel a scheduled or draft post. */
      cancel: async (id: string, options?: RequestOptions) => {
        return rb.execute(
          patchAdminSocialPostsByIdCancel,
          {
            path: { id },
            body: {
              data: { type: "social-post", id, attributes: {} },
            },
          },
          options,
        );
      },

      /** Retry a failed post. */
      retry: async (id: string, options?: RequestOptions) => {
        return rb.execute(
          patchAdminSocialPostsByIdRetry,
          {
            path: { id },
            body: {
              data: { type: "social-post", id, attributes: {} },
            },
          },
          options,
        );
      },

      /** Update SEO metadata on a post. */
      updateSeo: async (
        id: string,
        attributes: {
          seo_score?: number;
          seo_analysis?: Record<string, unknown>;
        },
        options?: RequestOptions,
      ) => {
        return rb.execute(
          patchAdminSocialPostsByIdSeo,
          {
            path: { id },
            body: {
              data: { type: "social-post", id, attributes },
            },
          },
          options,
        );
      },

      /** List scheduled posts. */
      listScheduled: async (options?: RequestOptions) => {
        return rb.execute(getAdminSocialPostsScheduled, {}, options);
      },

      /** List posts by account. */
      listByAccount: async (
        socialAccountId: string,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          getAdminSocialPostsAccountBySocialAccountId,
          { path: { social_account_id: socialAccountId } },
          options,
        );
      },

      /** List posts by campaign. */
      listByCampaign: async (
        socialCampaignId: string,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          getAdminSocialPostsCampaignBySocialCampaignId,
          { path: { social_campaign_id: socialCampaignId } },
          options,
        );
      },

      /** List all posts for a workspace. */
      listByWorkspace: async (
        workspaceId: string,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          getAdminSocialPostsWorkspaceByWorkspaceId,
          { path: { workspace_id: workspaceId } },
          options,
        );
      },
    },

    /** Post engagement metrics — impressions, reach, likes, comments, and more. */
    metrics: {
      /** Get a single metric record by ID. */
      get: async (id: string, options?: RequestOptions) => {
        return rb.execute(getAdminSocialMetricsById, { path: { id } }, options);
      },

      /** List all metrics for a post. */
      listByPost: async (socialPostId: string, options?: RequestOptions) => {
        return rb.execute(
          getAdminSocialMetricsPostBySocialPostId,
          { path: { social_post_id: socialPostId } },
          options,
        );
      },

      /** Get the latest metric per collection window for a post. */
      latestForPost: async (socialPostId: string, options?: RequestOptions) => {
        return rb.execute(
          getAdminSocialMetricsPostBySocialPostIdLatest,
          { path: { social_post_id: socialPostId } },
          options,
        );
      },

      /** Aggregate metrics across all posts for a social account. */
      byAccount: async (socialAccountId: string, options?: RequestOptions) => {
        return rb.execute(
          getAdminSocialMetricsAccountBySocialAccountId,
          { path: { social_account_id: socialAccountId } },
          options,
        );
      },

      /** Aggregate metrics across all posts in a social campaign. */
      byCampaign: async (
        socialCampaignId: string,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          getAdminSocialMetricsCampaignBySocialCampaignId,
          { path: { social_campaign_id: socialCampaignId } },
          options,
        );
      },
    },

    /** AI-powered social campaigns — generate master copy and adapt for platforms. */
    campaigns: {
      /**
       * List all social campaigns across workspaces.
       *
       * @param options - Optional request-level overrides.
       * @returns A promise that resolves to an array of social campaign records.
       */
      list: async (options?: RequestOptions) => {
        return rb.execute(getAdminSocialCampaigns, {}, options);
      },

      /**
       * Fetch a single social campaign by its unique ID.
       *
       * @param id - The unique identifier of the social campaign to retrieve.
       * @param options - Optional request-level overrides.
       * @returns A promise that resolves to the matching social campaign record.
       */
      get: async (id: string, options?: RequestOptions) => {
        return rb.execute(
          getAdminSocialCampaignsById,
          { path: { id } },
          options,
        );
      },

      /**
       * Create a new social campaign.
       *
       * The campaign starts in `draft` status. Use `generateMasterCopy` to
       * produce AI-generated content from the content brief, then
       * `adaptForPlatforms` to create platform-specific posts.
       *
       * @param workspaceId - Workspace ID (passed as query parameter for tenant resolution).
       * @param attributes - Campaign attributes (name, content_brief, etc.).
       * @param options - Optional request-level overrides.
       * @returns A promise that resolves to the newly created social campaign record.
       *
       * @example
       * ```typescript
       * const admin = new GptAdmin({ apiKey: 'sk_srv_...' });
       *
       * const campaign = await admin.social.campaigns.create('ws_abc123', {
       *   name: 'Product Launch',
       *   content_brief: 'Exciting new features for Q2',
       *   target_platforms: ['instagram', 'linkedin'],
       * });
       * ```
       */
      create: async (
        workspaceId: string,
        attributes: SocialCampaignCreateAttributes,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          postAdminSocialCampaigns,
          {
            query: { workspace_id: workspaceId },
            body: {
              data: { type: "social-campaign", attributes },
            },
          },
          options,
        );
      },

      /**
       * Update an existing social campaign's attributes (PATCH semantics).
       *
       * @param id - The unique identifier of the social campaign to update.
       * @param attributes - Key/value map of attributes to change.
       * @param options - Optional request-level overrides.
       * @returns A promise that resolves to the updated social campaign record.
       *
       * @example
       * ```typescript
       * const admin = new GptAdmin({ apiKey: 'sk_srv_...' });
       *
       * const updated = await admin.social.campaigns.update('sc_abc123', {
       *   content_brief: 'Updated brief with new messaging',
       *   target_platforms: ['instagram', 'linkedin', 'twitter'],
       * });
       * ```
       */
      update: async (
        id: string,
        attributes: SocialCampaignUpdateAttributes,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          patchAdminSocialCampaignsById,
          {
            path: { id },
            body: {
              data: { type: "social-campaign", id, attributes },
            },
          },
          options,
        );
      },

      /**
       * Permanently delete a social campaign.
       *
       * @param id - The unique identifier of the social campaign to delete.
       * @param options - Optional request-level overrides.
       * @returns A promise that resolves once the campaign has been deleted.
       *
       * @example
       * ```typescript
       * const admin = new GptAdmin({ apiKey: 'sk_srv_...' });
       *
       * await admin.social.campaigns.delete('sc_abc123');
       * ```
       */
      delete: async (id: string, options?: RequestOptions) => {
        return rb.execute(
          deleteAdminSocialCampaignsById,
          { path: { id } },
          options,
        );
      },

      /**
       * Schedule a social campaign for future publishing.
       *
       * Transitions the campaign to `scheduled` status with the given timestamp.
       *
       * @param id - The unique identifier of the social campaign to schedule.
       * @param scheduledAt - The ISO 8601 datetime at which to publish the campaign.
       * @param options - Optional request-level overrides.
       * @returns A promise that resolves to the updated social campaign record.
       *
       * @example
       * ```typescript
       * const admin = new GptAdmin({ apiKey: 'sk_srv_...' });
       *
       * const scheduled = await admin.social.campaigns.schedule(
       *   'sc_abc123',
       *   '2026-04-01T09:00:00Z',
       * );
       * ```
       */
      schedule: async (
        id: string,
        scheduledAt: string,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          patchAdminSocialCampaignsByIdSchedule,
          {
            path: { id },
            body: {
              data: {
                type: "social-campaign",
                id,
                attributes: { scheduled_at: scheduledAt },
              },
            },
          },
          options,
        );
      },

      /**
       * Cancel a social campaign.
       *
       * Only campaigns in `draft`, `generating`, `review`, or `scheduled`
       * status can be cancelled.
       *
       * @param id - The unique identifier of the social campaign to cancel.
       * @param options - Optional request-level overrides.
       * @returns A promise that resolves to the updated social campaign record.
       *
       * @example
       * ```typescript
       * const admin = new GptAdmin({ apiKey: 'sk_srv_...' });
       *
       * const cancelled = await admin.social.campaigns.cancel('sc_abc123');
       * console.log(cancelled.attributes.status); // 'cancelled'
       * ```
       */
      cancel: async (id: string, options?: RequestOptions) => {
        return rb.execute(
          patchAdminSocialCampaignsByIdCancel,
          {
            path: { id },
            body: {
              data: { type: "social-campaign", id, attributes: {} },
            },
          },
          options,
        );
      },

      /**
       * Generate master copy from the campaign's content brief using AI.
       *
       * Transitions the campaign to `review` status on success. The generated
       * master copy is stored on the campaign and can then be adapted for
       * individual platforms via `adaptForPlatforms`.
       *
       * @param id - The unique identifier of the social campaign.
       * @param workspaceId - The workspace ID that owns the campaign.
       * @param options - Optional request-level overrides.
       * @returns A promise resolving to the generation result including master copy text.
       *
       * @example
       * ```typescript
       * const admin = new GptAdmin({ apiKey: 'sk_srv_...' });
       *
       * const result = await admin.social.campaigns.generateMasterCopy(
       *   'sc_abc123',
       *   'ws_abc123',
       * );
       * console.log(result.master_copy);
       * ```
       */
      generateMasterCopy: async (
        id: string,
        workspaceId: string,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          postAdminSocialCampaignsByIdGenerateMasterCopy,
          {
            path: { id },
            body: {
              data: {
                campaign_id: id,
                workspace_id: workspaceId,
              },
            },
          },
          options,
        );
      },

      /**
       * Adapt the campaign's master copy for each target platform.
       *
       * Creates individual SocialPost draft records for each platform with
       * platform-appropriate tone, length, and formatting.
       *
       * @param id - The unique identifier of the social campaign.
       * @param workspaceId - The workspace ID that owns the campaign.
       * @param socialAccountId - The social account to attribute the created posts to.
       * @param options - Optional request-level overrides.
       * @returns A promise resolving to the adaptation result with post creation counts.
       *
       * @example
       * ```typescript
       * const admin = new GptAdmin({ apiKey: 'sk_srv_...' });
       *
       * const result = await admin.social.campaigns.adaptForPlatforms(
       *   'sc_abc123',
       *   'ws_abc123',
       *   'sa_xyz',
       * );
       * console.log(result.posts_created, result.failures);
       * ```
       */
      adaptForPlatforms: async (
        id: string,
        workspaceId: string,
        socialAccountId: string,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          postAdminSocialCampaignsByIdAdaptForPlatforms,
          {
            path: { id },
            body: {
              data: {
                campaign_id: id,
                workspace_id: workspaceId,
                social_account_id: socialAccountId,
              },
            },
          },
          options,
        );
      },

      /**
       * Preview adapted copy per platform without creating SocialPost records.
       *
       * Returns the AI-adapted text for each target platform so the user can
       * review before committing via `adaptForPlatforms`.
       *
       * @param id - Campaign UUID.
       * @param workspaceId - Workspace UUID.
       * @param options - Optional request options.
       * @returns `{ adaptations: [{ platform: string, content: string }] }`
       *
       * @example
       * ```typescript
       * const admin = new GptAdmin({ apiKey: 'sk_srv_...' });
       *
       * const result = await admin.social.campaigns.previewAdaptations(
       *   'sc_abc123',
       *   'ws_abc123',
       * );
       * console.log(result.adaptations); // [{platform: 'instagram', content: '...'}, ...]
       * ```
       */
      previewAdaptations: async (
        id: string,
        workspaceId: string,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          postAdminSocialCampaignsByIdPreviewAdaptations,
          {
            path: { id },
            body: {
              data: {
                campaign_id: id,
                workspace_id: workspaceId,
              },
            },
          },
          options,
        );
      },
    },

    /** Trending content discovery — snapshots, items, and watch alerts. */
    trending: {
      /**
       * Get a trending snapshot by ID.
       * @param id - Snapshot UUID
       * @returns TrendingSnapshot with metadata (query, enrichment level, item count)
       * @example
       * ```typescript
       * const snapshot = await admin.social.trending.get('snap_abc');
       * ```
       */
      get: async (id: string, options?: RequestOptions) => {
        return rb.execute(
          getAdminSocialTrendingHistoryById,
          { path: { id } },
          options,
        );
      },

      /**
       * List trending snapshots for a workspace.
       * @param workspaceId - Workspace UUID
       * @returns Array of TrendingSnapshot records
       * @example
       * ```typescript
       * const snapshots = await admin.social.trending.listByWorkspace('ws_abc');
       * ```
       */
      listByWorkspace: async (
        workspaceId: string,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          getAdminSocialTrendingHistoryWorkspaceByWorkspaceId,
          { path: { workspace_id: workspaceId } },
          options,
        );
      },

      /**
       * Create a trending snapshot (trigger on-demand collection).
       * @param attributes - Snapshot attributes (workspace_id, enrichment_level, etc.)
       * @returns Created TrendingSnapshot
       * @example
       * ```typescript
       * const snapshot = await admin.social.trending.create({
       *   workspace_id: 'ws_abc',
       *   enrichment_level: 'scored',
       *   fetched_at: new Date().toISOString(),
       * });
       * ```
       */
      create: async (
        attributes: {
          workspace_id: string;
          enrichment_level?: "raw" | "scored" | "summarized" | "full";
          query?: string;
          source_breakdown?: Record<string, unknown>;
          item_count?: number;
          fetched_at?: string;
        },
        options?: RequestOptions,
      ) => {
        return rb.execute(
          postAdminSocialTrendingHistory,
          {
            body: {
              data: { type: "trending-snapshot", attributes },
            },
          },
          options,
        );
      },

      /** Delete a trending snapshot by ID. */
      delete: async (id: string, options?: RequestOptions) => {
        return rb.execute(
          deleteAdminSocialTrendingHistoryById,
          { path: { id } },
          options,
        );
      },

      /**
       * List trending snapshots within a date range.
       * @param from - Start date (ISO 8601 string)
       * @param to - End date (ISO 8601 string)
       * @returns Array of TrendingSnapshot records in the range
       * @example
       * ```typescript
       * const snapshots = await admin.social.trending.listByDateRange(
       *   '2026-03-01T00:00:00Z',
       *   '2026-03-21T23:59:59Z',
       * );
       * ```
       */
      listByDateRange: async (
        from: string,
        to: string,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          getAdminSocialTrendingHistoryRange,
          { query: { from, to } },
          options,
        );
      },

      /** Trending snapshot items — individual content pieces within a snapshot. */
      items: {
        /** Get a single trending item by ID. */
        get: async (id: string, options?: RequestOptions) => {
          return rb.execute(
            getAdminSocialTrendingItemsById,
            { path: { id } },
            options,
          );
        },

        /**
         * List trending items for a snapshot.
         * @param snapshotId - Parent snapshot UUID
         * @returns Array of TrendingSnapshotItem records
         * @example
         * ```typescript
         * const items = await admin.social.trending.items.listBySnapshot('snap_abc');
         * ```
         */
        listBySnapshot: async (
          snapshotId: string,
          options?: RequestOptions,
        ) => {
          return rb.execute(
            getAdminSocialTrendingItemsSnapshotBySnapshotId,
            { path: { snapshot_id: snapshotId } },
            options,
          );
        },
      },
    },

    /** Trending watch alerts — monitor topics and get notified on matches. */
    watches: {
      /**
       * Get a trending watch by ID.
       * @param id - Watch UUID
       * @returns TrendingWatch with conditions and notification config
       */
      get: async (id: string, options?: RequestOptions) => {
        return rb.execute(
          getAdminSocialTrendingWatchesById,
          { path: { id } },
          options,
        );
      },

      /**
       * List trending watches for a workspace.
       * @param workspaceId - Workspace UUID
       * @returns Array of TrendingWatch records
       * @example
       * ```typescript
       * const watches = await admin.social.watches.listByWorkspace('ws_abc');
       * ```
       */
      listByWorkspace: async (
        workspaceId: string,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          getAdminSocialTrendingWatchesWorkspaceByWorkspaceId,
          { path: { workspace_id: workspaceId } },
          options,
        );
      },

      /**
       * Create a new trending watch alert.
       * @param attributes - Watch attributes (name, query, conditions, notification_config)
       * @returns Created TrendingWatch
       * @example
       * ```typescript
       * const watch = await admin.social.watches.create({
       *   workspace_id: 'ws_abc',
       *   name: 'AI Industry News',
       *   query: 'artificial intelligence',
       *   conditions: { min_score: 0.7 },
       *   notification_config: { channel: 'webhook', url: 'https://...' },
       * });
       * ```
       */
      create: async (
        attributes: {
          workspace_id: string;
          name: string;
          query: string;
          sources?: string[];
          conditions?: Record<string, unknown>;
          notification_config?: Record<string, unknown>;
          enabled?: boolean;
        },
        options?: RequestOptions,
      ) => {
        return rb.execute(
          postAdminSocialTrendingWatches,
          {
            body: {
              data: { type: "trending-watch", attributes },
            },
          },
          options,
        );
      },

      /**
       * Update a trending watch.
       * @param id - Watch UUID
       * @param attributes - Fields to update (name, query, conditions, enabled, etc.)
       */
      update: async (
        id: string,
        attributes: {
          name?: string;
          query?: string;
          sources?: string[];
          conditions?: Record<string, unknown>;
          notification_config?: Record<string, unknown>;
          enabled?: boolean;
        },
        options?: RequestOptions,
      ) => {
        return rb.execute(
          patchAdminSocialTrendingWatchesById,
          {
            path: { id },
            body: {
              data: { type: "trending-watch", id, attributes },
            },
          },
          options,
        );
      },

      /** Delete a trending watch by ID. */
      delete: async (id: string, options?: RequestOptions) => {
        return rb.execute(
          deleteAdminSocialTrendingWatchesById,
          { path: { id } },
          options,
        );
      },

      /**
       * Mark a watch as triggered (updates last_triggered_at).
       * @param id - Watch UUID
       */
      markTriggered: async (id: string, options?: RequestOptions) => {
        return rb.execute(
          patchAdminSocialTrendingWatchesByIdMarkTriggered,
          {
            path: { id },
            body: {
              data: { type: "trending-watch", id, attributes: {} },
            },
          },
          options,
        );
      },
    },
  };
}
